<template>
  <b-modal
    centered
    no-close-on-backdrop
    v-model="editorShown"
    :title="insurance ? 'Edit Insurance' : 'Add Insurance'"
    header-bg-variant="primary"
    header-text-variant="white"
    @ok="submitInsurance"
    :size="'md-lg'"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <div class="insurance-form-container">
      <b-row>
        <b-col cols="6">
          <b-form-group
            label="Insurance Company*"
            :state="formErrors.company.length == 0 ? null : false"
            :invalid-feedback="formErrors.company.join(', ')"
          >
            <template v-slot:label>
              Insurance Company <span class="text-danger">*</span>
              <b-link
                v-if="hasAccess('insuranceCompanies')"
                class="float-right"
                @click="showInsuranceCompanyModal = true"
              >
                + Add New Insurance
              </b-link>
            </template>
            <v-select
              :class="
                insuranceCompanies.length == 0
                  ? 'insurance-company-picker-hide-options'
                  : ''
              "
              placeholder="Search for insurance companies"
              v-model="form.company"
              :options="insuranceCompanies"
              @search="fetchInsuranceCompanies"
              ><template #no-options="{ search }">
                {{
                  search && search.length > 3
                    ? "Sorry, no matching options"
                    : ""
                }}
              </template>
              <template #open-indicator>
                <span></span>
              </template>
            </v-select>
          </b-form-group>
          <b-form-group
            label="Plan"
            :state="formErrors.plan.length == 0 ? null : false"
            :invalid-feedback="formErrors.plan.join(', ')"
          >
            <template v-slot:label>
              Plan
              <span v-if="systemSetting.saisystemsEnabled" class="text-danger"
                >*</span
              >
              <b-link
                v-if="hasAccess('insuranceCompanies')"
                class="float-right"
                @click="showInsuranceCompanyPlanModal = true"
              >
                + Add New Plan
              </b-link>
            </template>
            <v-select v-model="plan" :options="plans" />
          </b-form-group>
        </b-col>
        <b-col cols="6" class="">
          <b-form-group label="Insurance Card">
            <div class="d-flex justify-content-end">
              <div class="mr-2">
                <image-picker
                  v-model="form.insuranceCardFrontBase64"
                  :serverImageUrl="form.insuranceCardFrontUrl"
                  labelText="Card Front"
                />
              </div>
              <div>
                <image-picker
                  v-model="form.insuranceCardBackBase64"
                  :serverImageUrl="form.insuranceCardBackUrl"
                  labelText="Card Back"
                />
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div
            v-if="form.company"
            class="d-flex flex-column bg-extra-light-gray rounded p-3 custom-shadow mb-3"
          >
            <div class="d-flex justify-content-between">
              <h5 class="mb-0">{{ form.company.data.name || "" }}</h5>
              <span> PID: {{ form.company.data.payerId }} </span>
            </div>
            <div class="d-flex flex-wrap">
              <div class="mr-1">
                <small>{{ companyFirstData }}</small>
              </div>
            </div>
            <div class="d-flex flex-wrap">
              <div class="mr-1">
                <small>{{ companySecondData }}</small>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                label="Member #"
                :state="formErrors.memberIdent.length == 0 ? null : false"
                :invalid-feedback="formErrors.memberIdent.join(', ')"
              >
                <template
                  v-if="
                    isPracticeSuiteActive || systemSetting.saisystemsEnabled
                  "
                  v-slot:label
                >
                  Member # <span class="text-danger">*</span>
                </template>
                <b-form-input v-model="form.memberIdent" trim maxLength="100" />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Group #"
                :state="formErrors.groupNo.length == 0 ? null : false"
                :invalid-feedback="formErrors.groupNo.join(', ')"
              >
                <template v-slot:label>
                  Group #
                </template>
                <b-form-input v-model="form.groupNo" trim maxLength="100" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                label="Coverage"
                :state="formErrors.coverage.length == 0 ? null : false"
                :invalid-feedback="formErrors.coverage.join(', ')"
              >
                <template v-slot:label>
                  Coverage <span class="text-danger">*</span>
                </template>
                <v-select
                  v-model="form.coverage"
                  :options="insuranceCoverages"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Payer Type"
                :state="formErrors.payerType.length == 0 ? null : false"
                :invalid-feedback="formErrors.payerType.join(', ')"
              >
                <template v-slot:label>
                  Payer Type <span class="text-danger">*</span>
                </template>
                <v-select
                  v-model="form.payerType"
                  :options="payerTypes"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="10">
          <b-row>
            <b-col md="4">
              <b-form-group
                label="Copay"
                :state="formErrors.copay.length == 0 ? null : false"
                :invalid-feedback="formErrors.copay.join(', ')"
              >
                <b-form-input
                  v-model="form.copay"
                  type="number"
                  min="0"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" v-if="isEnableDeductible">
              <b-form-group
                label="Deductible/Co-Insurance payment amt."
                :state="
                  formErrors.deductibleCoInsurance.length == 0 ? null : false
                "
                :invalid-feedback="formErrors.deductibleCoInsurance.join(', ')"
              >
                <b-form-input
                  v-model="form.deductibleCoInsurance"
                  type="number"
                  min="0"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Relationship"
                :state="formErrors.relationship.length == 0 ? null : false"
                :invalid-feedback="formErrors.relationship.join(', ')"
              >
                <template v-slot:label>
                  Relationship to Subscriber <span class="text-danger">*</span>
                </template>
                <v-select
                  v-model="form.relationship"
                  :options="relationships"
                ></v-select>
              </b-form-group>
              <b-form-group
                v-if="isOtherRelationSelected"
                :state="formErrors.relationshipOther.length == 0 ? null : false"
                :invalid-feedback="formErrors.relationshipOther.join(', ')"
              >
                <b-form-input
                  placeholder="Other"
                  v-model="form.relationshipOther"
                  trim
                  maxLength="100"
                >
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col> </b-col>
      </b-row>
      <div
        v-if="
          !form.relationship || form.relationship.label.toLowerCase() != 'self'
        "
      >
        <b-row>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group
                  label="Insured DOB"
                  :state="formErrors.insuredDob.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredDob.join(', ')"
                >
                  <template v-slot:label>
                    Insured DOB
                    <span
                      v-if="
                        isPracticeSuiteActive || systemSetting.saisystemsEnabled
                      "
                      class="text-danger"
                      >*</span
                    >
                  </template>
                  <date-picker
                    v-model="form.insuredDob"
                    :errors="formErrors.insuredDob"
                  ></date-picker>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Insured First Name"
                  :state="
                    formErrors.insuredFirstName.length == 0 ? null : false
                  "
                  :invalid-feedback="formErrors.insuredFirstName.join(', ')"
                >
                  <template v-slot:label>
                    Insured First Name <span class="text-danger">*</span>
                  </template>
                  <b-form-input
                    v-model="form.insuredFirstName"
                    trim
                    maxLength="100"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group label="Insured Middle Name">
                  <b-form-input
                    v-model="form.insuredMiddleName"
                    trim
                    maxLength="100"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Insured Last Name"
                  :state="formErrors.insuredLastName.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredLastName.join(', ')"
                >
                  <template v-slot:label>
                    Insured Last Name <span class="text-danger">*</span>
                  </template>
                  <b-form-input
                    v-model="form.insuredLastName"
                    trim
                    maxLength="100"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group
                  label="Insured SSN"
                  :state="formErrors.insuredSsn.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredSsn.join(', ')"
                >
                  <template v-slot:label>
                    Insured SSN
                  </template>
                  <b-form-input
                    v-model="form.insuredSsn"
                    v-facade="'###-##-####'"
                    trim
                    :state="formErrors.insuredSsn.length == 0 ? null : false"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Insured Legal Sex"
                  :state="formErrors.insuredGender.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredGender.join(', ')"
                >
                  <template v-slot:label>
                    Insured Legal Sex
                    <span
                      v-if="
                        isPracticeSuiteActive || systemSetting.saisystemsEnabled
                      "
                      class="text-danger"
                      >*</span
                    >
                  </template>
                  <v-select
                    v-model="form.insuredGender"
                    :options="genders"
                  ></v-select>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group
                  label="Insured Phone"
                  :state="formErrors.insuredPhone.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredPhone.join(', ')"
                >
                  <template v-slot:label>
                    Insured Phone
                  </template>
                  <phone-input
                    ref="phoneInput"
                    defaultCountry="US"
                    v-model="form.insuredPhone"
                    trim
                    :state="formErrors.insuredPhone.length == 0 ? null : false"
                  ></phone-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Insured Address Line 1"
                  :state="
                    formErrors.insuredAddressLine1.length == 0 ? null : false
                  "
                  :invalid-feedback="formErrors.insuredAddressLine1.join(', ')"
                >
                  <template v-slot:label>
                    Insured Address Line 1
                    <span v-if="isPracticeSuiteActive" class="text-danger"
                      >*</span
                    >
                  </template>
                  <b-form-input
                    v-model="form.insuredAddressLine1"
                    trim
                    maxLength="150"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group
                  label="Insured Address Line 2"
                  :state="
                    formErrors.insuredAddressLine2.length == 0 ? null : false
                  "
                  :invalid-feedback="formErrors.insuredAddressLine2.join(', ')"
                >
                  <b-form-input
                    v-model="form.insuredAddressLine2"
                    trim
                    maxLength="150"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Insured City"
                  :state="formErrors.insuredCity.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredCity.join(', ')"
                >
                  <template v-slot:label>
                    Insured City
                    <span v-if="isPracticeSuiteActive" class="text-danger"
                      >*</span
                    >
                  </template>
                  <b-form-input
                    v-model="form.insuredCity"
                    trim
                    maxLength="100"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <b-row>
              <b-col>
                <b-form-group
                  label="Insured State"
                  :state="formErrors.insuredState.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredState.join(', ')"
                >
                  <template v-slot:label>
                    Insured State
                    <span v-if="isPracticeSuiteActive" class="text-danger"
                      >*</span
                    >
                  </template>
                  <v-select
                    v-model="form.insuredState"
                    :options="states"
                  ></v-select>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  label="Insured Zipcode"
                  :state="formErrors.insuredZipcode.length == 0 ? null : false"
                  :invalid-feedback="formErrors.insuredZipcode.join(', ')"
                >
                  <template v-slot:label>
                    Insured Zipcode
                    <span v-if="isPracticeSuiteActive" class="text-danger"
                      >*</span
                    >
                  </template>
                  <b-form-input
                    v-model="form.insuredZipcode"
                    v-facade="'#####'"
                    trim
                    :state="
                      formErrors.insuredZipcode.length == 0 ? null : false
                    "
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <b-row>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                label="Effective Date"
                :state="formErrors.effectiveDate.length == 0 ? null : false"
                :invalid-feedback="formErrors.effectiveDate.join(', ')"
              >
                <template v-slot:label>
                  Effective Date
                </template>
                <date-picker
                  v-model="form.effectiveDate"
                  :errors="formErrors.effectiveDate"
                >
                </date-picker>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Termination Date"
                :state="formErrors.terminationDate.length == 0 ? null : false"
                :invalid-feedback="formErrors.terminationDate.join(', ')"
              >
                <template v-slot:label>
                  Termination Date
                </template>
                <date-picker
                  v-model="form.terminationDate"
                  :errors="formErrors.terminationDate"
                ></date-picker>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                label="Paid Through Date"
                :state="formErrors.paidThroughDate.length == 0 ? null : false"
                :invalid-feedback="formErrors.paidThroughDate.join(', ')"
              >
                <template v-slot:label>
                  Paid Through Date
                </template>
                <date-picker
                  v-model="form.paidThroughDate"
                  :errors="formErrors.paidThroughDate"
                >
                </date-picker>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group label="Grace Period">
                <v-select
                  v-model="form.gracePeriod"
                  :options="gracePeriodValues"
                  placeholder=""
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Notes">
            <b-form-textarea v-model="form.notes"></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <insurance-company-modal
      v-if="showInsuranceCompanyModal"
      v-model="showInsuranceCompanyModal"
      @onModify="insuranceCompanyOnModify"
    />
    <insurance-company-plan-modal
      v-if="showInsuranceCompanyPlanModal"
      v-model="showInsuranceCompanyPlanModal"
      @onModify="insuranceCompanyPlanOnModify"
    />
  </b-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import insuranceCompanyServer from "../../server/insuranceCompany";
import insuranceCompanyPlanServer from "../../server/insuranceCompanyPlan";
import {
  validateRequired,
  validateDate,
  validateZipcode,
  validatePhoneNumber,
  validateSSN,
  validateNumberLessEqualThan
} from "../../helpers/validation";
import DatePicker from "../controls/DatePicker";
import PhoneInput from "../controls/PhoneInput.vue";
import InsuranceCompanyModal from "../setting/InsuranceCompanyModal.vue";
import InsuranceCompanyPlanModal from "../setting/InsuranceCompanyPlanModal.vue";
import ImagePicker from "../controls/ImagePicker";

export default {
  name: "PatientInsuranceForm",
  components: {
    DatePicker,
    InsuranceCompanyModal,
    InsuranceCompanyPlanModal,
    ImagePicker,
    PhoneInput
  },
  props: ["show", "insurance"],
  data() {
    return {
      insuranceCompanies: [],
      plans: [],
      gracePeriodValues: [
        {
          value: true,
          label: "Yes"
        },
        {
          value: false,
          label: "No"
        }
      ],
      insuranceCoverages: [
        {
          value: 0,
          label: "Primary"
        },
        {
          value: 1,
          label: "Secondary"
        },
        {
          value: 3,
          label: "Tertiary"
        },
        {
          value: 2,
          label: "Other"
        }
      ],
      form: {
        company: null,
        plan: null,
        memberIdent: "",
        groupNo: "",
        coverage: null,
        relationship: null,
        relationshipOther: null,
        insuredDob: null,
        insuredFirstName: null,
        insuredMiddleName: null,
        insuredLastName: null,
        insuredSsn: "",
        insuredPhone: "",
        insuredAddressLine1: "",
        insuredAddressLine2: "",
        insuredCity: "",
        insuredState: null,
        insuredGender: null,
        insuredZipcode: "",
        payerType: null,
        copay: null,
        deductibleCoInsurance: null,
        effectiveDate: null,
        terminationDate: null,
        paidThroughDate: null,
        gracePeriod: null,
        notes: "",
        insuranceCardFrontBase64: null,
        insuranceCardBackBase64: null,
        deleteInsuranceCardFront: false,
        deleteInsuranceCardBack: false
      },
      formErrors: {
        company: [],
        plan: [],
        memberIdent: [],
        groupNo: [],
        coverage: [],
        relationship: [],
        relationshipOther: [],
        insuredDob: [],
        insuredSsn: [],
        insuredPhone: [],
        insuredFirstName: [],
        insuredLastName: [],
        insuredAddressLine1: [],
        insuredAddressLine2: [],
        insuredCity: [],
        insuredState: [],
        insuredZipcode: [],
        insuredGender: [],
        payerType: [],
        copay: [],
        effectiveDate: [],
        terminationDate: [],
        paidThroughDate: [],
        deductibleCoInsurance: []
      },

      showInsuranceCompanyModal: false,
      showInsuranceCompanyPlanModal: false
    };
  },
  mounted() {
    if (this.insurance) this.bindData(this.insurance);
    else {
      const selfRelationShip = this.relationships.find(
        r => r.label.toLowerCase() == "self"
      );
      if (selfRelationShip) {
        this.form.relationship = selfRelationShip;
      }
    }
    this.fetchPayerTypes();
    this.fetchPlans();
  },
  methods: {
    ...mapActions("client", ["fetchPayerTypes"]),
    hasAccess(key) {
      return this.hasCurrentUserPermissionAccess("settings", key);
    },
    bindData: function(data) {
      this.form = {
        ...data,
        company: data.company
          ? {
              value: data.company.id,
              label: data.company.name,
              data: data.company
            }
          : null,
        plan: data.plan
          ? {
              value: data.plan.id,
              label: data.plan.name,
              data: data.plan
            }
          : null,
        coverage: this.insuranceCoverages.find(i => i.value == data.coverage),
        relationship: data.relationship
          ? {
              value: data.relationship.id,
              label: data.relationship.name
            }
          : null,
        relationshipOther: data.patientRelationshipOther,
        insuredState: data.insuredState
          ? {
              value: data.insuredState.id,
              label: data.insuredState.name
            }
          : null,
        insuredGender: data.insuredGender
          ? {
              value: data.insuredGender.id,
              label: data.insuredGender.name
            }
          : null,
        payerType: data.payerType
          ? {
              value: data.payerType.id,
              label: data.payerType.name
            }
          : null,
        copay: data.copay,
        deductibleCoInsurance: data.deductibleCoInsurance,
        gracePeriod: data.gracePeriod
          ? {
              value: data.gracePeriod.value,
              label: data.gracePeriod.name
            }
          : null,
        insuranceCardFrontId: data.insuranceCardFrontId,
        insuranceCardBackId: data.insuranceCardBackId,
        insuranceCardFrontBase64: data.insuranceCardFrontBase64,
        insuranceCardBackBase64: data.insuranceCardBackBase64
      };

      this.clearErrors();
    },
    submitInsurance: function(modalEvent) {
      modalEvent.preventDefault();

      if (this.validateInsuranceSubmit()) {
        const data = {
          ...this.form,
          company: this.form.company ? this.form.company.data : null,
          plan: this.form.plan
            ? {
                id: this.form.plan.value,
                name: this.form.plan.label
              }
            : null,
          coverage: this.form.coverage.value,
          relationship: this.form.relationship
            ? {
                id: this.form.relationship.value,
                name: this.form.relationship.label
              }
            : null,
          relationshipOther: !this.isOtherRelationSelected
            ? null
            : this.form.relationshipOther,
          insuredDob:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredDob
              : null,
          insuredFirstName:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredFirstName
              : "",
          insuredMiddleName:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredMiddleName
              : "",
          insuredLastName:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredLastName
              : "",
          insuredSsn:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredSsn?.replace(/[^0-9]+/g, "")
              : "",
          insuredPhone:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredPhone.trim().length >
                this.activeCountry.dialCode.length + 1
                ? this.form.insuredPhone
                : null
              : "",
          insuredAddressLine1:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredAddressLine1
              : "",
          insuredAddressLine2:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredAddressLine2
              : "",
          insuredCity:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredCity
              : "",
          insuredState:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredState
                ? {
                    id: this.form.insuredState.value,
                    name: this.form.insuredState.label
                  }
                : null
              : "",
          insuredZipcode:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredZipcode?.replace(/[^0-9]+/g, "")
              : "",
          insuredGender:
            !this.form.relationship ||
            this.form.relationship.label.toLowerCase() != "self"
              ? this.form.insuredGender
                ? {
                    id: this.form.insuredGender.value,
                    name: this.form.insuredGender.label
                  }
                : null
              : "",
          payerType: this.form.payerType
            ? {
                id: this.form.payerType.value,
                name: this.form.payerType.label
              }
            : "",
          copay: this.form.copay,
          deductibleCoInsurance: this.form.deductibleCoInsurance,
          effectiveDate: this.form.effectiveDate
            ? this.form.effectiveDate
            : null,
          terminationDate: this.form.terminationDate
            ? this.form.terminationDate
            : null,
          paidThroughDate: this.form.paidThroughDate
            ? this.form.paidThroughDate
            : null,
          gracePeriod: this.form.gracePeriod
            ? {
                value: this.form.gracePeriod.value,
                name: this.form.gracePeriod.label
              }
            : null,
          notes: this.form.notes ? this.form.notes : null,
          insuranceCardFrontBase64: this.form.insuranceCardFrontBase64,
          insuranceCardBackBase64: this.form.insuranceCardBackBase64,
          deleteInsuranceCardFront: this.form.deleteInsuranceCardFront,
          deleteInsuranceCardBack: this.form.deleteInsuranceCardBack
        };
        this.$emit("ok", data);
      }
    },
    clearErrors: function() {
      this.formErrors = {
        company: [],
        plan: [],
        memberIdent: [],
        groupNo: [],
        coverage: [],
        relationship: [],
        relationshipOther: [],
        insuredDob: [],
        insuredFirstName: [],
        insuredLastName: [],
        insuredSsn: [],
        insuredPhone: [],
        insuredAddressLine1: [],
        insuredAddressLine2: [],
        insuredCity: [],
        insuredState: [],
        insuredZipcode: [],
        insuredGender: [],
        payerType: [],
        copay: [],
        effectiveDate: [],
        terminationDate: [],
        paidThroughDate: [],
        deductibleCoInsurance: []
      };
    },
    validateInsuranceSubmit: function() {
      this.clearErrors();
      let withoutErrors = true;

      if (this.systemSetting.saisystemsEnabled) {
        this.formErrors.plan = [];
        if (!validateRequired(this.form.plan)) {
          this.formErrors.plan.push("This field is required");
          withoutErrors = false;
        }
      }

      if (!validateRequired(this.form.company)) {
        this.formErrors.company.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.payerType = [];
      if (!validateRequired(this.form.payerType)) {
        this.formErrors.payerType.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.copay = [];
      if (this.form.copay && validateNumberLessEqualThan(this.form.copay, 0)) {
        this.formErrors.copay.push("Copay must be a positive number");
        withoutErrors = false;
      }
      this.formErrors.deductibleCoInsurance = [];
      if (
        this.form.deductibleCoInsurance &&
        validateNumberLessEqualThan(this.form.deductibleCoInsurance, 0)
      ) {
        this.formErrors.deductibleCoInsurance.push(
          "Deductible/Co-Insurance must be a positive number"
        );
        withoutErrors = false;
      }

      if (
        this.form.effectiveDateStart &&
        !validateDate(this.form.effectiveDateStart)
      ) {
        this.formErrors.effectiveDateStart.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      if (
        this.form.effectiveDateEnd &&
        !validateDate(this.form.effectiveDateEnd)
      ) {
        this.formErrors.effectiveDateEnd.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      if (
        !validateRequired(this.form.memberIdent) &&
        (this.isPracticeSuiteActive || this.systemSetting.saisystemsEnabled)
      ) {
        this.formErrors.memberIdent.push("This field is required");
        withoutErrors = false;
      }

      if (!validateRequired(this.form.coverage)) {
        this.formErrors.coverage.push("This field is required");
        withoutErrors = false;
      }

      if (!validateRequired(this.form.relationship)) {
        this.formErrors.relationship.push("This field is required");
        withoutErrors = false;
      }

      if (
        this.form.relationship.label.toLowerCase() === "other" &&
        !validateRequired(this.form.relationshipOther)
      ) {
        this.formErrors.relationshipOther.push("This field is required");
        withoutErrors = false;
      }

      if (
        this.form.relationship &&
        this.form.relationship.label.toLowerCase() != "self"
      ) {
        if (!validateRequired(this.form.insuredFirstName)) {
          this.formErrors.insuredFirstName.push("This field is required");
          withoutErrors = false;
        }
        if (!validateRequired(this.form.insuredLastName)) {
          this.formErrors.insuredLastName.push("This field is required");
          withoutErrors = false;
        }
        if (this.form.insuredSsn && !validateSSN(this.form.insuredSsn)) {
          this.formErrors.insuredSsn.push(
            "This field must have a valid SSN format"
          );
          withoutErrors = false;
        }
        if (
          (this.isPracticeSuiteActive ||
            this.systemSetting.saisystemsEnabled) &&
          !validateRequired(this.form.insuredDob)
        ) {
          this.formErrors.insuredDob.push("This field is required");
          withoutErrors = false;
        }
        if (this.form.insuredDob && !validateDate(this.form.insuredDob)) {
          this.formErrors.insuredDob.push(
            "This field must have a valid date format"
          );
          withoutErrors = false;
        }
        if (
          (this.isPracticeSuiteActive ||
            this.systemSetting.saisystemsEnabled) &&
          !validateRequired(this.form.insuredGender)
        ) {
          this.formErrors.insuredGender.push("This field is required");
          withoutErrors = false;
        }
        this.formErrors.insuredZipcode = [];
        if (
          this.form.insuredZipcode &&
          !validateZipcode(this.form.insuredZipcode)
        ) {
          this.formErrors.insuredZipcode.push(
            "This field must have a valid Zipcode format"
          );
          withoutErrors = false;
        }
        this.formErrors.insuredPhone = [];
        if (
          this.form.insuredPhone.trim().length >
            this.activeCountry.dialCode.length + 1 &&
          !validatePhoneNumber(this.form.insuredPhone)
        ) {
          this.formErrors.insuredPhone.push(
            "This field must have a valid phone number"
          );
          withoutErrors = false;
        }

        if (
          this.isPracticeSuiteActive &&
          !validateRequired(this.form.insuredZipcode)
        ) {
          this.formErrors.insuredZipcode.push("This field is required");
          withoutErrors = false;
        }

        if (
          this.isPracticeSuiteActive &&
          !validateRequired(this.form.insuredAddressLine1)
        ) {
          this.formErrors.insuredAddressLine1.push("This field is required");
          withoutErrors = false;
        }

        this.formErrors.insuredCity = [];
        if (
          this.isPracticeSuiteActive &&
          !validateRequired(this.form.insuredCity)
        ) {
          this.formErrors.insuredCity.push("This field is required");
          withoutErrors = false;
        }

        this.formErrors.insuredState = [];
        if (
          this.isPracticeSuiteActive &&
          !validateRequired(this.form.insuredState)
        ) {
          this.formErrors.insuredState.push("This field is required");
          withoutErrors = false;
        }
      }

      if (this.form.effectiveDate && !validateDate(this.form.effectiveDate)) {
        this.formErrors.effectiveDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      if (
        this.form.terminationDate &&
        !validateDate(this.form.terminationDate)
      ) {
        this.formErrors.terminationDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      if (
        this.form.paidThroughDate &&
        !validateDate(this.form.paidThroughDate)
      ) {
        this.formErrors.paidThroughDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    fetchInsuranceCompanies: async function(search, loading) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.insuranceCompanies = [];
      }

      this.lastTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const insuranceCompanies = await insuranceCompanyServer.query(
            { searchPhrase: search, page: 0, pageSize: 1000000 },
            {
              "Hide-Loading": true
            }
          );
          this.insuranceCompanies = insuranceCompanies.records.map(
            insuranceCompany => ({
              value: insuranceCompany.id,
              label: insuranceCompany.name,
              data: insuranceCompany
            })
          );
        } else this.insuranceCompanies = [];
        loading(false);
      }, 1000);
    },
    async fetchPlans() {
      const list = await insuranceCompanyPlanServer.query();
      this.plans = list
        ? list.map(p => ({
            value: p.id,
            label: p.name,
            data: p
          }))
        : [];
    },
    insuranceCompanyOnModify(params) {
      this.form.company = {
        value: params.insuranceCompany.id,
        label: params.insuranceCompany.name,
        data: params.insuranceCompany
      };
    },
    async insuranceCompanyPlanOnModify(params) {
      await this.fetchPlans();
      this.form.plan = {
        value: params.insuranceCompanyPlan.id,
        label: params.insuranceCompanyPlan.name,
        data: params.insuranceCompanyPlan
      };
    }
  },
  computed: {
    ...mapState("client", {
      relationships: state =>
        state.relationships.map(relationship => ({
          value: relationship.id,
          label: relationship.name
        })),
      states: state =>
        state.states.map(state => ({
          value: state.id,
          label: state.name
        })),
      genders: state =>
        state.genders.map(gender => ({
          value: gender.id,
          label: gender.name
        })),
      payerTypes: state =>
        state.payerTypes.map(payerType => ({
          value: payerType.id,
          label: payerType.name
        }))
    }),
    ...mapState("layout", {
      isPracticeSuiteActive: state => state.isPracticeSuiteActive,
      systemSetting: state => state.systemSetting
    }),
    ...mapGetters("layout", ["hasCurrentUserPermissionAccess"]),
    insuranceCardFrontComputed() {
      return this.form.insuranceCardFrontBase64;
    },
    insuranceCardBackComputed() {
      return this.form.insuranceCardBackBase64;
    },
    isEnableDeductible() {
      return this.systemSetting.enableDeductible;
    },
    plan: {
      get() {
        return this.form.plan;
      },
      set(newValue) {
        this.form.plan = newValue;
      }
    },
    companyFirstData() {
      let data = "";
      if (this.form.company) {
        let components = [];
        if (this.form.company.data.phoneNumber)
          components.push(`Phone #: ${this.form.company.data.phoneNumber}`);
        if (this.form.company.data.email)
          components.push(`Email: ${this.form.company.data.email}`);
        if (this.form.company.data.fax)
          components.push(`Fax: ${this.form.company.data.fax}`);
        data = components.join(" | ");
      }
      return data;
    },
    companySecondData() {
      let data = "";
      if (this.form.company) {
        let components = [];
        if (this.form.company.data.addressLine1)
          components.push(`${this.form.company.data.addressLine1}`);
        if (this.form.company.data.addressLine2)
          components.push(`${this.form.company.data.addressLine2}`);
        if (this.form.company.data.city)
          components.push(`${this.form.company.data.city}`);
        if (this.form.company.data.state)
          components.push(`${this.form.company.data.state.name}`);
        if (this.form.company.data.zipcode)
          components.push(`${this.form.company.data.zipcode}`);
        data = "Address: " + components.join(", ");
      }
      return data;
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    isOtherRelationSelected: {
      get() {
        return this.form.relationship
          ? this.form.relationship.label === "Other"
          : false;
      }
    },
    activeCountry() {
      return this.$refs.phoneInput.$refs.telInput.activeCountry;
    }
  },
  watch: {
    insuranceCardFrontComputed(val) {
      if (!val && this.form.insuranceCardFrontId)
        this.form.deleteInsuranceCardFront = true;
    },
    insuranceCardBackComputed(val) {
      if (!val && this.form.insuranceCardBackId)
        this.form.deleteInsuranceCardBack = true;
    }
  }
};
</script>

<style scoped>
.custom-shadow {
  box-shadow: 5px 5px 5px -5px rgba(0, 0, 0, 0.5);
}
.insurance-form-container {
  max-height: calc(100vh - 225px);
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
<style>
.insurance-company-picker-hide-options .vs__dropdown-menu {
  display: none;
}
.insurance-company-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
</style>
